import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { GlassInput } from '@/components/ui/GlassInput'
import { api } from '@/lib/api'
import { workspacesQueryOptions } from '@/lib/queryOptions'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/settings/workspaces')({
  loader: async ({ context: { queryClient } }) => {
    await queryClient.ensureQueryData(workspacesQueryOptions())
  },
  component: WorkspacesPage,
})

function WorkspacesPage() {
  const t = useT()
  const qc = useQueryClient()
  const { data } = useQuery(workspacesQueryOptions())
  const [name, setName] = useState('')
  const [sector, setSector] = useState('')
  const [creatingError, setCreatingError] = useState('')
  const [themeDraft, setThemeDraft] = useState<Record<string, string>>({})
  const [domainDraft, setDomainDraft] = useState<Record<string, string>>({})

  const workspaces = data?.workspaces
  const activeWorkspaceId = data?.activeWorkspaceId

  const createMutation = useMutation({
    mutationFn: api.workspaces.create,
    onSuccess: () => {
      setName('')
      setSector('')
      setCreatingError('')
      qc.invalidateQueries({ queryKey: ['workspaces'] })
    },
    onError: (err) => setCreatingError(err instanceof Error ? err.message : 'Failed'),
  })

  const switchMutation = useMutation({
    mutationFn: api.workspaces.switch,
    onSuccess: () => {
      qc.invalidateQueries()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: { customDomain?: string; themeConfig?: Record<string, unknown> } }) =>
      api.workspaces.update(id, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['workspaces'] }),
  })

  const sortedWorkspaces = useMemo(
    () => [...(workspaces ?? [])].sort((a, b) => (a.workspaceType === 'SHARED_OPS' ? -1 : 0) - (b.workspaceType === 'SHARED_OPS' ? -1 : 0)),
    [workspaces],
  )

  return (
    <RootLayout>
      <div className="space-y-6 max-w-4xl">
        <div>
          <h1 className="text-xl font-bold text-white">{t('settings.workspaces.title')}</h1>
          <p className="text-sm text-white/40 mt-0.5">{t('settings.workspaces.subtitle')}</p>
        </div>

        <GlassCard className="space-y-4">
          <h2 className="font-semibold text-white text-sm">{t('settings.workspaces.createTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput
              label={t('settings.workspaces.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.workspaces.namePlaceholder')}
            />
            <GlassInput
              label={t('settings.workspaces.sectorLabel')}
              value={sector}
              onChange={(e) => setSector(e.target.value)}
              placeholder={t('settings.workspaces.sectorPlaceholder')}
            />
          </div>
          {creatingError && <p className="text-red-400 text-sm">{creatingError}</p>}
          <GlassButton
            onClick={() => createMutation.mutate({ name, sector: sector || undefined })}
            loading={createMutation.isPending}
            disabled={!name}
          >
            {t('settings.workspaces.createButton')}
          </GlassButton>
        </GlassCard>

        <div className="space-y-3">
          {sortedWorkspaces.map((workspace) => (
            <GlassCard key={workspace.id} className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-white text-sm">
                    {workspace.name}
                    {workspace.id === activeWorkspaceId && (
                      <span className="text-xs text-[var(--oc-accent-light)] ml-2">{t('settings.workspaces.active')}</span>
                    )}
                  </p>
                  <p className="text-xs text-white/45">
                    {workspace.workspaceType} · {workspace.role} · {workspace.sector}
                  </p>
                </div>
                {workspace.id !== activeWorkspaceId && (
                  <GlassButton
                    size="sm"
                    variant="secondary"
                    onClick={() => switchMutation.mutate(workspace.id)}
                    loading={switchMutation.isPending}
                  >
                    {t('settings.workspaces.switchButton')}
                  </GlassButton>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <GlassInput
                  label={t('settings.workspaces.customDomainLabel')}
                  value={domainDraft[workspace.id] ?? workspace.customDomain ?? ''}
                  onChange={(e) => setDomainDraft((prev) => ({ ...prev, [workspace.id]: e.target.value }))}
                  placeholder={t('settings.workspaces.customDomainPlaceholder')}
                />
                <GlassInput
                  label={t('settings.workspaces.themeAccentLabel')}
                  value={themeDraft[workspace.id] ?? String((workspace.themeConfig?.accentColor as string | undefined) ?? '')}
                  onChange={(e) => setThemeDraft((prev) => ({ ...prev, [workspace.id]: e.target.value }))}
                  placeholder={t('settings.workspaces.themeAccentPlaceholder')}
                />
              </div>

              <GlassButton
                size="sm"
                onClick={() => updateMutation.mutate({
                  id: workspace.id,
                  payload: {
                    customDomain: domainDraft[workspace.id] ?? workspace.customDomain ?? undefined,
                    themeConfig: {
                      ...(workspace.themeConfig ?? {}),
                      accentColor: themeDraft[workspace.id] ?? (workspace.themeConfig?.accentColor as string | undefined) ?? undefined,
                    },
                  },
                })}
                loading={updateMutation.isPending}
              >
                {t('settings.workspaces.saveWorkspace')}
              </GlassButton>
            </GlassCard>
          ))}
        </div>
      </div>
    </RootLayout>
  )
}
